

































































































import { Component, Ref, Vue, Watch } from "vue-property-decorator";
import BaseDialogComponent from "@/components/common/BaseDialog.vue";
import ConfirmationDialogComponent from "@/components/common/ConfirmationDialog.vue";
import { MiraInsurer } from "@/data/models/MiraInsurerAdministration";
import { saveMiraInsurer } from "@/data/repository/MiraInsurerAdministrationRepository";
import { SnackbarModule } from "@/store/modules/SnackbarModule";
import { formatDateToDayMonthYear } from "@/utils/DateHelper";
import {
  canSaveInsurerForm,
  insurerErrorMessage,
  insurerFieldError,
  insurerFormChanged,
  MiraInsurerForm,
  toInsurerForm,
  toInsurerSave,
} from "@/utils/MiraInsurerAdministration";
import { adminPortalChangeAccess } from "@/permissions/UserPermissions";
import { getReactiveActiveUser } from "@/data/repository/UserRepository";

/** What `show()` resolves with. */
export type MiraInsurerDialogResult = MiraInsurer | "CHANGED_MEANWHILE" | null;

/**
 * Shows, edits or creates one insurer of Mira's managed list (Track #2610040 P3). IRES Cloud is the
 * authority (`saveMiraInsurerForAdministration`): a taken key, a list changed meanwhile and a spelling
 * another insurer claims are refused there; these rules give the messages first.
 *
 * `show()` resolves with the saved insurer, `CHANGED_MEANWHILE` when IRES Cloud refused because the
 * insurer changed since it was loaded (the caller reloads), or null when closed without saving.
 */
@Component({ components: { "base-dialog": BaseDialogComponent, "confirmation-dialog": ConfirmationDialogComponent } })
export default class MiraInsurerDialogComponent extends Vue {
  @Ref("baseDialog")
  baseDialog!: BaseDialogComponent;

  @Ref("confirm")
  confirmDialog!: ConfirmationDialogComponent;

  busy = false;
  isEditMode = false;
  // Initialised, not `!`: the computeds below must track them from the first render (edit-mode-dialogs.md).
  stored: MiraInsurer | null = null;
  form: MiraInsurerForm = toInsurerForm(null);
  /** A field shows its error once it was left, never on an untouched new form or while typing. */
  touched: Record<"code" | "shortName" | "name", boolean> = { code: false, shortName: false, name: false };

  private resolve: ((result: MiraInsurerDialogResult) => void) | null = null;

  get isNew(): boolean {
    return this.stored === null;
  }

  get hasChanged(): boolean {
    return insurerFormChanged(this.stored, this.form);
  }

  get canSave(): boolean {
    return canSaveInsurerForm(this.form);
  }

  /** A field's message in edit mode only, and only once the field was touched. */
  errorFor(field: "code" | "shortName" | "name"): string[] {
    if (!this.isEditMode || !this.touched[field]) return [];
    const error = insurerFieldError(field, this.form);
    return error ? [this.$t(`admin.miraInsurers.fieldErrors.${error}`).toString()] : [];
  }

  touch(field: "code" | "shortName" | "name") {
    this.touched[field] = true;
  }

  formatDate(millis: number): string {
    return formatDateToDayMonthYear(new Date(millis)) ?? "–";
  }

  /** The shared IRES admin login sees the admin portal read-only (#2610071). */
  get mayEdit(): boolean {
    return adminPortalChangeAccess(getReactiveActiveUser());
  }

  /**
   * @param insurer - the insurer to show, or null to create one.
   * @param prefill - for a new insurer only: the form to start from (a review group's spellings, #2610125).
   */
  show(insurer: MiraInsurer | null, prefill: MiraInsurerForm | null = null): Promise<MiraInsurerDialogResult> {
    this.stored = insurer ? { ...insurer } : null;
    this.form = insurer === null && prefill ? { ...prefill } : toInsurerForm(insurer);
    this.touched = { code: false, shortName: false, name: false };
    this.isEditMode = insurer === null;
    this.baseDialog.show({
      title: this.titleText(),
      // No BEARBEITEN for the shared IRES admin login, which IRES Cloud refuses (#2610071).
      positiveButtonText: this.isEditMode || this.mayEdit
        ? this.$t(this.isEditMode ? "common.save" : "settings.buttons.change").toString()
        : null,
      // A pre-filled new insurer is a change from the start, so it opens with ABBRECHEN (edit-mode-dialogs.md).
      negativeButtonText: this.$t(this.isEditMode && this.hasChanged ? "common.abort" : "common.close").toString(),
      maxWidth: 600,
      persistent: this.isEditMode,
      closeOnSave: false,
    });
    return new Promise((resolve) => (this.resolve = resolve));
  }

  private titleText(): string {
    if (this.isNew) return this.$t("admin.miraInsurers.dialog.createTitle").toString();
    return this.$t(this.isEditMode ? "admin.miraInsurers.dialog.editTitle" : "admin.miraInsurers.dialog.viewTitle").toString();
  }

  /** SCHLIEßEN while nothing changed, ABBRECHEN after (edit-mode-dialogs.md). */
  @Watch("hasChanged")
  onHasChanged(hasChanged: boolean) {
    if (this.isEditMode) {
      this.baseDialog.negativeButtonText = this.$t(hasChanged ? "common.abort" : "common.close").toString();
    }
  }

  /** BEARBEITEN in view mode, SICHERN in edit mode. */
  onPositive() {
    if (!this.isEditMode) {
      this.isEditMode = true;
      this.baseDialog.title = this.titleText();
      this.baseDialog.positiveButtonText = this.$t("common.save").toString();
      this.baseDialog.persistent = true;
      return;
    }
    this.onSave();
  }

  /** SCHLIEßEN has already closed the dialog; ABBRECHEN asks first. */
  async onNegative() {
    if (!this.isEditMode || !this.hasChanged) {
      this.finish(null);
      return;
    }
    const discard = await this.confirmDialog.show({
      title: this.$t("common.confirmationDialog.unsavedChangesTitle").toString(),
      message: this.$t("common.confirmationDialog.unsavedChangesMessage").toString(),
      acceptMessage: this.$t("common.abort").toString(),
      rejectMessage: this.$t("common.back").toString(),
    });
    if (discard) {
      this.close(null);
    }
  }

  async onSave() {
    if (!this.hasChanged || !this.canSave) {
      return;
    }
    this.busy = true;
    try {
      const saved = await saveMiraInsurer(toInsurerSave(this.stored, this.form));
      SnackbarModule.showSuccess({ key: "admin.miraInsurers.saved" });
      this.close(saved);
    } catch (error) {
      const message = insurerErrorMessage(error);
      SnackbarModule.showError(message);
      // Someone changed the insurer meanwhile: close, so the list reloads and the user starts from the new values.
      if (message.key.endsWith("CHANGED_MEANWHILE")) {
        this.close("CHANGED_MEANWHILE");
      }
    } finally {
      this.busy = false;
    }
  }

  close(result: MiraInsurerDialogResult) {
    this.baseDialog.isShowing = false;
    this.finish(result);
  }

  private finish(result: MiraInsurerDialogResult) {
    this.resolve?.(result);
    this.resolve = null;
  }
}
